/* eslint-disable no-param-reassign */
import axios, { AxiosInstance } from 'axios';
import Constants from 'expo-constants';
import { Platform } from 'react-native';

import { Env } from 'app/hooks/useStore';
import AuthService from 'app/services/AuthService';
import Logger from 'app/services/Logger';

const MODULE = '[GuestCenterClient]';

class GuestCenterApiError extends Error {
  response: unknown;

  constructor(message: string, response: unknown) {
    super(message);
    this.name = 'CitypassApiError';
    this.response = response;
  }
}

export default function GuestCenterClient(): AxiosInstance {
  const client = axios.create({
    baseURL: envToBaseURL(Constants.expoConfig?.extra?.environment),
    headers: {
      'X-CC-App-Version': Constants.expoConfig?.version ?? '',
      'X-CC-App-Platform': Platform.OS,
      'X-CC-App-Session': Constants.sessionId,
    },
  });

  client.interceptors.request.use(async (config) => {
    config.baseURL = client.defaults.baseURL;

    if (config.headers) {
      try {
        const token = await AuthService.getToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        Logger.error(`${MODULE} token error`, { error });
      }
    }

    Logger.debug(`${MODULE} request`, {
      method: config.method?.toUpperCase(),
      url: `${config.baseURL}${config.url}`,
      ...(config.data && { data: config.data }),
      withAuth: !!config.headers?.Authorization,
    });

    return config;
  });

  let refreshPromise: Promise<string | undefined> | null = null;

  function getFreshToken(): Promise<string | undefined> {
    if (!refreshPromise) {
      refreshPromise = AuthService.refreshToken().finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise ?? Promise.resolve(undefined);
  }

  client.interceptors.response.use(
    (res) => {
      const { config, status, data } = res;

      Logger.debug(`${MODULE} response`, {
        method: config.method?.toUpperCase(),
        url: `${config.baseURL}${config.url}`,
        status,
      });

      if (typeof data === 'string' && data.includes('<html')) {
        Logger.debug(`${MODULE} response`, { data });
        throw new GuestCenterApiError('unexpected html response', data);
      }

      if (
        typeof data === 'object' &&
        data !== null &&
        'success' in data &&
        data.success === false
      ) {
        Logger.debug(`${MODULE} response`, { data });
        throw data;
      }

      return res;
    },
    async (error) => {
      const originalRequest = error.config;
      const isAuthenticated = Boolean(originalRequest?.headers?.Authorization);
      const is401 = error.response?.status === 401;

      // eslint-disable-next-line no-underscore-dangle
      if (is401 && isAuthenticated && !originalRequest._retry) {
        // eslint-disable-next-line no-underscore-dangle
        originalRequest._retry = true;
        try {
          const freshToken = await getFreshToken();

          if (freshToken) {
            originalRequest.headers.Authorization = `Bearer ${freshToken}`;
            return client(originalRequest);
          }
        } catch {
          // refresh failed — fall through to log and rethrow the original 401
        }
      }

      const level = is401 ? 'warn' : 'error';
      Logger[level](`${MODULE} response`, {
        method: error?.config?.method?.toUpperCase(),
        url: `${error?.config?.baseURL}${error?.config?.url}`,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        readyState: error.request?.readyState,
      });

      throw error;
    }
  );

  return client;
}

export const setBaseURL = (client: AxiosInstance, env: Env) => {
  client.defaults.baseURL = envToBaseURL(env);
};

function envToBaseURL(env: Env) {
  return Constants.expoConfig?.extra?.guestCenter?.apiBaseURLTemplate.replace('{env}', env);
}
